import { useQuery } from '@tanstack/react-query';
import { pisoService } from '../services/pisoService';
import { CreatePisoForm, UpdatePisoForm, Piso } from '../types';
import { useCrudResource } from './useCrudResource';

export const usePisos = () => {
  const crud = useCrudResource<Piso, CreatePisoForm, UpdatePisoForm>({
    queryKey: ['pisos'],
    invalidateKeys: [['pisos-activos']],
    fetchList: async () => {
      const response = await pisoService.getPisos();
      if (!response.success) {
        throw new Error(response.message || 'Error al cargar los pisos');
      }
      return response.data || [];
    },
    create: async (piso) => {
      const response = await pisoService.createPiso(piso);
      if (!response.success) {
        throw new Error(response.message || 'Error al crear el piso');
      }
      return response.data;
    },
    update: async (id, piso) => {
      const response = await pisoService.updatePiso(id, piso);
      if (!response.success) {
        throw new Error(response.message || 'Error al actualizar el piso');
      }
      return response.data;
    },
    remove: async (id) => {
      const response = await pisoService.deletePiso(id);
      if (!response.success) {
        throw new Error(response.message || 'Error al eliminar el piso');
      }
    },
  });

  const {
    data: pisosActivos,
    isLoading: isLoadingActivos,
    error: errorActivos,
    refetch: refetchPisosActivos,
  } = useQuery({
    queryKey: ['pisos-activos'],
    queryFn: async () => {
      const response = await pisoService.getPisosActivos();
      if (!response.success) {
        throw new Error(response.message || 'Error al cargar los pisos activos');
      }
      return response.data || [];
    },
    retry: 2,
    retryDelay: 1000,
  });

  const updatePiso = (
    vars: { id: number; piso: UpdatePisoForm },
    options?: Parameters<typeof crud.update>[1]
  ) => crud.update({ id: vars.id, data: vars.piso }, options);

  return {
    pisos: crud.items,
    pisosActivos: pisosActivos || [],
    isLoading: crud.isLoading,
    isLoadingActivos,
    error: crud.error,
    errorActivos,
    createPiso: crud.create,
    updatePiso,
    deletePiso: crud.remove,
    isCreating: crud.isCreating,
    isUpdating: crud.isUpdating,
    isDeleting: crud.isRemoving,
    refetchPisos: crud.refetch,
    refetchPisosActivos,
    createError: crud.createError,
    updateError: crud.updateError,
    deleteError: crud.removeError,
  };
};
